import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { People as PeopleIcon } from '@mui/icons-material';
import api from '../../services/api';
import type { UserEconomyRow } from '../../types/accountUserEconomy';
import { formatAccountMetricValue } from '../../utils/accountMetricFormat';
import { formatUsersSectionTitle } from '../../utils/accountDashboardFocus';
import { useAccountDashboard } from './AccountDashboardContext';

interface AccountUsersTableProps {
  accountId: string;
  economyVisible: boolean;
  canEdit: boolean;
  onManageUsers: () => void;
  usersRefreshKey?: number;
}

interface FallbackUserRow {
  userId: string;
  displayName: string | null;
  email: string;
  role: string;
}

export default function AccountUsersTable({
  accountId,
  economyVisible,
  canEdit,
  onManageUsers,
  usersRefreshKey = 0
}: AccountUsersTableProps) {
  const { metric, granularity, anchor, focusWindow, currencyCode } = useAccountDashboard();

  const [rows, setRows] = useState<UserEconomyRow[] | FallbackUserRow[]>([]);
  const [loading, setLoading] = useState(false);

  const sectionTitle = formatUsersSectionTitle(focusWindow?.label, granularity);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      if (economyVisible) {
        const res = await api.getAccountUserEconomy(accountId, { metric, granularity, anchor });
        if (res.success) {
          setRows(res.data.users ?? []);
        } else {
          setRows([]);
        }
      } else {
        const res = await api.getAccountUsers(accountId);
        const users = (res.data ?? []).map(
          (u: { id: string; displayName?: string; email: string; role: string }) => ({
            userId: u.id,
            displayName: u.displayName ?? null,
            email: u.email,
            role: u.role
          })
        );
        setRows(users);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, economyVisible, metric, granularity, anchor]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers, usersRefreshKey]);

  const formatRegistered = (value: number) => formatAccountMetricValue(value, metric, currencyCode);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{sectionTitle}</Typography>
        {canEdit && (
          <Button size="small" startIcon={<PeopleIcon />} onClick={onManageUsers}>
            Manage
          </Button>
        )}
      </Box>

      {loading ? (
        <LinearProgress />
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No users assigned to this account.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                {economyVisible && <TableCell align="right">Registered</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.userId}>
                  <TableCell>
                    <Typography variant="body2">{row.displayName || row.email}</Typography>
                    {row.displayName && (
                      <Typography variant="caption" color="text.secondary">
                        {row.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={row.role} size="small" />
                  </TableCell>
                  {economyVisible && (
                    <TableCell align="right">
                      {formatRegistered((row as UserEconomyRow).registered ?? 0)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
